import type { Room } from './room';

/**
 * O lado do contorno que **não** é parede (Fase 24, fatia 9 · D25).
 *
 * Todo lado traçado virava parede, e a varanda com sacada não fechava: ou ganhava uma
 * parede de 2,90 na frente — alvenaria, reboco e rodapé que a obra não vai fazer —, ou
 * ficava sem ser traçada. Cada lado passa a ter um tipo:
 *
 * - **Parede**, o de sempre, e o que todo lado é enquanto ninguém disser outra coisa;
 * - **Guarda-corpo**, o metálico ou de vidro comprado por metro: não soma alvenaria nem
 *   face, e é uma peça que se mede — comprimento, área (altura × comprimento) e contagem;
 * - **Aberto**, o lado sem nada, como a circulação que dá no hall.
 *
 * Nos dois últimos o rodapé e o perímetro não correm (RN-267); o piso e o teto seguem
 * inteiros, porque o contorno continua fechando o ambiente.
 *
 * A mureta com guarda-corpo em cima **não** é um tipo de lado: é parede, com a altura dela,
 * e o guarda-corpo é um marcador na parede (`Wall.railingHeight`).
 */
export type EdgeKind = 'PAREDE' | 'GUARDA_CORPO' | 'ABERTO';

export const EDGE_KIND_LABEL: Readonly<Record<EdgeKind, string>> = {
  PAREDE: 'Parede',
  GUARDA_CORPO: 'Guarda-corpo',
  ABERTO: 'Aberto',
};

/** A altura de guarda-corpo que a norma pede na varanda — o padrão, e editável. */
export const DEFAULT_RAILING_HEIGHT = 1.1;

/**
 * Um lado sem parede, pelo índice dele no contorno — o mesmo `edgeIndex` da parede (RN-243).
 *
 * Só o guarda-corpo tem altura: é dela que sai a área que se compra. O aberto não tem
 * nada a medir, e uma altura nele seria número sem serventia esperando virar engano.
 */
export type EdgeWithoutWall =
  | { readonly edgeIndex: number; readonly kind: 'GUARDA_CORPO'; readonly height: number }
  | { readonly edgeIndex: number; readonly kind: 'ABERTO' };

/**
 * Os lados sem parede, conferidos — venham de onde vierem.
 *
 * Serve ao `jsonb` do banco, que volta como qualquer coisa, e ao cadastro. O que não aponta
 * lado nenhum (índice negativo ou quebrado) ou não diz um tipo conhecido fica de fora: o
 * lado volta a ser parede, que é o que ele era antes de alguém mexer — e não derruba o
 * ambiente inteiro por causa de um campo que tem plano B. O guarda-corpo sem altura válida
 * volta com a de padrão, pelo mesmo motivo.
 *
 * Um registro por lado, o último valendo, na ordem do contorno. Lista vazia é ausência.
 */
export function normalizeEdgesWithoutWall(valor: unknown): readonly EdgeWithoutWall[] | undefined {
  if (!Array.isArray(valor)) return undefined;

  const porLado = new Map<number, EdgeWithoutWall>();
  for (const bruto of valor) {
    if (typeof bruto !== 'object' || bruto === null) continue;
    const { edgeIndex, kind, height } = bruto as {
      edgeIndex?: unknown;
      kind?: unknown;
      height?: unknown;
    };
    if (typeof edgeIndex !== 'number' || !Number.isInteger(edgeIndex) || edgeIndex < 0) continue;
    if (kind === 'ABERTO') {
      porLado.set(edgeIndex, { edgeIndex, kind });
    } else if (kind === 'GUARDA_CORPO') {
      const altura =
        typeof height === 'number' && Number.isFinite(height) && height > 0
          ? height
          : DEFAULT_RAILING_HEIGHT;
      porLado.set(edgeIndex, { edgeIndex, kind, height: altura });
    }
  }

  if (porLado.size === 0) return undefined;
  return [...porLado.values()].sort((um, outro) => um.edgeIndex - outro.edgeIndex);
}

/** O lado sem parede neste índice, se ele for um. */
export function edgeWithoutWallAt(
  room: Pick<Room, 'edgesWithoutWall'>,
  edgeIndex: number,
): EdgeWithoutWall | undefined {
  return room.edgesWithoutWall?.find((lado) => lado.edgeIndex === edgeIndex);
}

/** O tipo do lado: o que foi dito, ou parede — que é o que todo lado é por padrão. */
export function edgeKindAt(room: Pick<Room, 'edgesWithoutWall'>, edgeIndex: number): EdgeKind {
  return edgeWithoutWallAt(room, edgeIndex)?.kind ?? 'PAREDE';
}

/**
 * O ambiente com um lado de outro tipo — só o registro do lado; a parede é de quem chama.
 *
 * Parede **tira** o registro em vez de gravá-lo: ausente é parede, e dois jeitos de dizer a
 * mesma coisa divergiriam na primeira comparação. O guarda-corpo guarda a altura que já
 * tinha quando só o tipo muda de novo para ele, e nasce com a de padrão.
 */
export function withEdgeKind<T extends Pick<Room, 'edgesWithoutWall'>>(
  room: T,
  edgeIndex: number,
  kind: EdgeKind,
  height?: number,
): T {
  const anterior = edgeWithoutWallAt(room, edgeIndex);
  const outros = (room.edgesWithoutWall ?? []).filter((lado) => lado.edgeIndex !== edgeIndex);

  const novo: EdgeWithoutWall | undefined =
    kind === 'PAREDE'
      ? undefined
      : kind === 'ABERTO'
        ? { edgeIndex, kind }
        : {
            edgeIndex,
            kind,
            height:
              height ??
              (anterior?.kind === 'GUARDA_CORPO' ? anterior.height : DEFAULT_RAILING_HEIGHT),
          };

  const lista = normalizeEdgesWithoutWall(novo === undefined ? outros : [...outros, novo]);
  const { edgesWithoutWall: _antigos, ...resto } = room;
  return (lista === undefined ? resto : { ...resto, edgesWithoutWall: lista }) as T;
}
